<script setup lang="ts">
import { sanitizeAndCamelCaseTitle } from '@@/bits/sanitize_and_transform_text'
import { SnackbarLoadingStatus, SnackbarNotificationType } from '@@/enums'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzLoadingSpinnerWithCheckmark from '@@/library/v4/components/OzLoadingSpinnerWithCheckmark.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import type { SnackbarLoadingState } from '@@/types'

withDefaults(
  defineProps<{
    /**
     * Unique id associated with the snackbar
     */
    uid: string
    /**
     * Displayed message of the snackbar
     */
    message: string
    /**
     * Type of snakcbar notification
     */
    notificationType?: SnackbarNotificationType
    /**
     * Icon to display on the snackbar
     */
    iconName?: string
    /**
     * Text for clickable action
     */
    actionText?: string
    /**
     * Dark mode
     */
    darkMode?: boolean | 'auto'
    shouldTruncate?: boolean
    loadingState?: SnackbarLoadingState
  }>(),
  {
    notificationType: SnackbarNotificationType.success,
    iconName: undefined,
    actionText: undefined,
    darkMode: 'auto',
    shouldTruncate: false,
    loadingState: () => ({
      progress: 0,
      status: SnackbarLoadingStatus.loading,
    }),
  },
)

const emit = defineEmits<{
  (name: 'click', event: MouseEvent): void
  (name: 'action-text-click', event: MouseEvent): void
}>()

const actionTextClick = (event): void => {
  emit('action-text-click', event)
}
</script>

<script lang="ts">
export default {}
</script>

<template>
  <div v-if="actionText">
    <div class="relative flex">
      <OzBaseButton
        :class="[
          // default styles
          'w-max max-w-[calc(100vw-64px)] py-2.5 px-3.5 flex items-center rounded-lg mb-2 pointer-events-auto font-sans',
          (notificationType === SnackbarNotificationType.success ||
            notificationType === SnackbarNotificationType.spinner) && {
            'bg-canary-500 hover:bg-canary-400': darkMode === true,
            'bg-grape-500 hover:bg-grape-600': darkMode === false,
            'bg-grape-500 hover:bg-grape-600 dark:bg-canary-500 dark:hover:bg-canary-400': darkMode === 'auto',
          },
          // error snackbar styles
          notificationType === SnackbarNotificationType.error && {
            'bg-scarlet-600 hover:bg-danger-100': darkMode === true,
            'bg-danger-100 hover:bg-scarlet-600': darkMode === false,
            'bg-danger-100 hover:bg-scarlet-600 dark:bg-scarlet-600 dark:hover:bg-danger-100': darkMode === 'auto',
          },
        ]"
        @click="$emit('click', $event)"
      >
        <OzIcon
          v-if="iconName"
          :class="[
            // default styles
            'p-1 me-2 rounded-full',

            // success snackbar styles
            (notificationType === SnackbarNotificationType.success ||
              notificationType === SnackbarNotificationType.spinner) && {
              'text-dark-text-100 bg-light-text-400': darkMode === true,
              'text-light-text-100 bg-dark-text-400': darkMode === false,
              'text-light-text-100 bg-dark-text-400 dark:text-dark-text-100 dark:bg-light-text-400':
                darkMode === 'auto',
            },

            // error snackbar styles
            notificationType === SnackbarNotificationType.error && {
              'text-light-text-100': true,
              'bg-light-text-400': darkMode === true,
              'bg-dark-text-400': darkMode === false,
              'bg-dark-text-400 dark:bg-light-text-400': darkMode === 'auto',
            },
          ]"
          :name="iconName"
          :size="12"
        />
        <OzLoadingSpinnerWithCheckmark
          v-else-if="notificationType === SnackbarNotificationType.spinner"
          variation="snackbar"
          :size="20"
          :x-spinner-rail="true"
          :progress="loadingState.progress"
          :dark-mode="darkMode"
          :checkmark-icon="loadingState.status === SnackbarLoadingStatus.success ? 'checkmark' : 'error'"
          :is-checkmark="loadingState.status !== SnackbarLoadingStatus.loading"
          class="me-2"
        />
        <div :class="shouldTruncate && 'flex overflow-hidden'">
          <span
            :class="[
              // default styles
              'text-body-small break-words',

              shouldTruncate && 'truncate',

              // success snackbar styles
              (notificationType === SnackbarNotificationType.success ||
                notificationType === SnackbarNotificationType.spinner) && {
                'text-dark-text-100': darkMode === true,
                'text-light-text-100': darkMode === false,
                'text-light-text-100 dark:text-dark-text-100': darkMode === 'auto',
              },

              // error snackbar styles
              notificationType === SnackbarNotificationType.error && 'text-light-text-100 font-semibold',
            ]"
            >{{ message }}
          </span>
        </div>
        <p aria-hidden="true" class="invisible ms-2">{{ actionText }}</p>
      </OzBaseButton>
      <OzPlainButton
        :data-testid="`snackbar${sanitizeAndCamelCaseTitle(actionText)}`"
        :text="actionText"
        :color-scheme="OzPlainButtonColorScheme.Bare"
        :size-preset="OzPlainButtonSizePreset.Bare"
        :class="[
          'absolute top-2.5 end-3.5 pointer-events-auto',
          'p-0 text-body-small font-semibold',
          'z-10',
          {
            'text-dark-text-200': darkMode === true,
            'text-light-text-200': darkMode === false,
            'text-light-text-200 dark:text-dark-text-200': darkMode === 'auto',
          },
          {
            'hover-hover:hover:text-dark-text-100': darkMode === true,
            'hover-hover:hover:text-light-text-100': darkMode === false,
            'hover-hover:hover:text-light-text-100 hover-hover:dark:hover:text-dark-text-100': darkMode === 'auto',
          },
        ]"
        @click.stop.prevent="actionTextClick"
      />
    </div>
  </div>
  <OzBaseButton
    v-else
    :class="[
      // default styles
      'w-max max-w-[calc(100vw-64px)] py-2.5 px-3.5 flex items-center rounded-lg mb-2 pointer-events-auto font-sans',
      (notificationType === SnackbarNotificationType.success ||
        notificationType === SnackbarNotificationType.spinner) && {
        'bg-canary-500 hover:bg-canary-400': darkMode === true,
        'bg-grape-500 hover:bg-grape-600': darkMode === false,
        'bg-grape-500 hover:bg-grape-600 dark:bg-canary-500 dark:hover:bg-canary-400': darkMode === 'auto',
      },
      // error snackbar styles
      notificationType === SnackbarNotificationType.error && {
        'bg-scarlet-600 hover:bg-danger-100': darkMode === true,
        'bg-danger-100 hover:bg-scarlet-600': darkMode === false,
        'bg-danger-100 hover:bg-scarlet-600 dark:bg-scarlet-600 dark:hover:bg-danger-100': darkMode === 'auto',
      },
    ]"
    @click="$emit('click', $event)"
  >
    <OzIcon
      v-if="iconName"
      :class="[
        // default styles
        'p-1 me-2 rounded-full',

        // success snackbar styles
        (notificationType === SnackbarNotificationType.success ||
          notificationType === SnackbarNotificationType.spinner) && {
          'text-dark-text-100 bg-light-text-400': darkMode === true,
          'text-light-text-100 bg-dark-text-400': darkMode === false,
          'text-light-text-100 bg-dark-text-400 dark:text-dark-text-100 dark:bg-light-text-400': darkMode === 'auto',
        },

        // error snackbar styles
        notificationType === SnackbarNotificationType.error && {
          'text-light-text-100': true,
          'bg-light-text-400': darkMode === true,
          'bg-dark-text-400': darkMode === false,
          'bg-dark-text-400 dark:bg-light-text-400': darkMode === 'auto',
        },
      ]"
      :name="iconName"
      :size="12"
    />
    <OzLoadingSpinnerWithCheckmark
      v-else-if="notificationType === SnackbarNotificationType.spinner"
      variation="snackbar"
      :size="20"
      :x-spinner-rail="true"
      :progress="loadingState.progress"
      :dark-mode="darkMode"
      :checkmark-icon="loadingState.status === SnackbarLoadingStatus.success ? 'checkmark' : 'error'"
      :is-checkmark="loadingState.status !== SnackbarLoadingStatus.loading"
      class="me-2"
    />
    <div :class="shouldTruncate && 'flex overflow-hidden'">
      <span
        :class="[
          // default styles
          'text-body-small break-words',

          shouldTruncate && 'truncate',

          // success snackbar styles
          (notificationType === SnackbarNotificationType.success ||
            notificationType === SnackbarNotificationType.spinner) && {
            'text-dark-text-100': darkMode === true,
            'text-light-text-100': darkMode === false,
            'text-light-text-100 dark:text-dark-text-100': darkMode === 'auto',
          },

          // error snackbar styles
          notificationType === SnackbarNotificationType.error && 'text-light-text-100 font-semibold',
        ]"
        >{{ message }}
      </span>
    </div>
  </OzBaseButton>
</template>
